// Common colors
import { CSSProperties } from 'react';

export const colors = {
  primary: '#007bff',
  success: '#28a745',
  warning: '#fff3cd',
  warningText: '#856404',
  warningBorder: '#ffeeba',
  danger: '#ff5555',
  lightGray: '#f5f5f5',
  mediumGray: '#f0f0f0',
  darkGray: '#666',
  border: '#ddd',
  lightBorder: '#eee',
  white: '#fff',
  black: '#000'
};

// Base styles
export const baseStyles = {
  fontFamily: 'monospace',
  fontSize: {
    xs: '10px',
    sm: '11px',
    base: '12px'
  },
  borderRadius: {
    sm: '3px',
    md: '4px',
    circle: '50%'
  },
  padding: {
    xs: '2px 6px',
    sm: '4px 8px',
    md: '8px',
    lg: '10px',
    xl: '12px',
    xxl: '16px'
  },
  margin: {
    none: 0,
    xs: '4px',
    sm: '6px',
    md: '8px',
    lg: '12px',
    xl: '16px'
  },
  zIndex: {
    overlay: 10000
  }
};

// Button styles
export const buttonStyles = {
  base: {
    border: 'none',
    borderRadius: baseStyles.borderRadius.sm,
    cursor: 'pointer',
    fontSize: baseStyles.fontSize.xs,
    padding: baseStyles.padding.xs,
    marginRight: baseStyles.margin.xs
  },
  default: {
    background: colors.mediumGray,
    color: colors.black
  },
  danger: {
    background: colors.danger,
    color: colors.white
  }
};

// Panel styles
export const panelStyles = {
  base: {
    position: 'fixed' as const,
    zIndex: 10000,
    backgroundColor: '#fff',
    fontFamily: 'monospace',
    fontSize: '12px'
  } as CSSProperties,
  default: {
    top: '10px',
    right: '10px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    padding: '8px',
    width: '500px',
    maxHeight: '400px',
    overflowY: 'auto' as const, // Use 'as const' for string literal types
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  } as CSSProperties,
  fullScreen: {
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    display: 'flex' as const,
    flexDirection: 'column' as const
  } as CSSProperties,
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: baseStyles.padding.md,
    marginBottom: baseStyles.margin.md
  } as CSSProperties,
  headerFullScreen: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: baseStyles.padding.md,
    borderBottom: `1px solid ${colors.lightBorder}`,
    marginBottom: '0'
  } as CSSProperties,
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '0'
  } as CSSProperties,
  contentFullScreen: {
    flex: 1,
    overflowY: 'auto',
    padding: baseStyles.padding.xxl
  } as CSSProperties
};

// Collapsed view styles
export const collapsedViewStyles = {
  container: {
    position: 'fixed',
    top: '10px',
    right: '10px',
    zIndex: baseStyles.zIndex.overlay,
    cursor: 'pointer',
    backgroundColor: colors.white,
    border: '1px solid #ccc',
    borderRadius: baseStyles.borderRadius.md,
    padding: baseStyles.padding.sm,
    fontSize: baseStyles.fontSize.base,
    fontFamily: baseStyles.fontFamily,
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  } as CSSProperties,
  timeIndicator: {
    color: colors.darkGray
  } as CSSProperties,
  requestCount: {
    backgroundColor: colors.mediumGray,
    borderRadius: baseStyles.borderRadius.circle,
    width: '18px',
    height: '18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: baseStyles.fontSize.xs
  } as CSSProperties,
  warningCount: {
    backgroundColor: colors.warning,
    color: colors.warningText,
    borderRadius: baseStyles.borderRadius.circle,
    width: '18px',
    height: '18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: baseStyles.fontSize.xs
  } as CSSProperties
};

// Requests view styles
export const requestsViewStyles = {
  requestItem: {
    padding: baseStyles.padding.md,
    borderBottom: `1px solid ${colors.lightBorder}`,
    cursor: 'pointer',
    backgroundColor: colors.white
  } as CSSProperties,
  requestHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  } as CSSProperties,
  duration: {
    fontWeight: 'bold'
  },
  name: {
    color: colors.darkGray,
    flexGrow: 1,
    marginLeft: baseStyles.margin.md
  } as CSSProperties,
  badgeContainer: {
    display: 'flex',
    gap: '8px'
  } as CSSProperties,
  bulletWarningBadge: {
    color: colors.warningText,
    backgroundColor: colors.warning,
    padding: baseStyles.padding.xs,
    borderRadius: baseStyles.borderRadius.sm,
    cursor: 'pointer',
    fontSize: baseStyles.fontSize.xs
  } as CSSProperties,
  memoryBadge: {
    color: colors.success,
    cursor: 'pointer',
    backgroundColor: '#f0f8f0',
    padding: baseStyles.padding.xs,
    borderRadius: baseStyles.borderRadius.sm,
    fontSize: baseStyles.fontSize.xs
  } as CSSProperties,
  sqlBadge: {
    color: colors.primary,
    backgroundColor: '#e6f2ff',
    padding: baseStyles.padding.xs,
    borderRadius: baseStyles.borderRadius.sm,
    cursor: 'pointer',
    fontSize: baseStyles.fontSize.xs
  } as CSSProperties
};

// Bullet warnings view styles
export const bulletWarningsViewStyles = {
  warningContainer: {
    backgroundColor: colors.warning,
    padding: baseStyles.padding.xl,
    marginBottom: baseStyles.margin.md,
    borderRadius: baseStyles.borderRadius.md,
    border: `1px solid ${colors.warningBorder}`
  } as CSSProperties,
  warningTitle: {
    margin: '0 0 8px 0',
    color: colors.warningText
  } as CSSProperties,
  infoItem: {
    marginBottom: baseStyles.margin.sm
  } as CSSProperties,
  callerCode: {
    backgroundColor: '#fffdf5',
    padding: baseStyles.padding.md,
    borderRadius: baseStyles.borderRadius.sm,
    overflowX: 'auto',
    fontSize: baseStyles.fontSize.sm,
    margin: '6px 0 0 0'
  } as CSSProperties
};

// Memory view styles
export const memoryViewStyles = {
  container: {
    backgroundColor: colors.lightGray,
    padding: baseStyles.padding.xxl,
    borderRadius: baseStyles.borderRadius.md,
    marginBottom: baseStyles.margin.xl
  } as CSSProperties,
  title: {
    margin: '0 0 12px 0'
  } as CSSProperties,
  dataRow: {
    marginBottom: baseStyles.margin.md
  } as CSSProperties,
  progressLabel: {
    marginBottom: baseStyles.margin.xs
  } as CSSProperties,
  progressContainer: {
    width: '100%',
    height: '24px',
    backgroundColor: '#e9ecef',
    borderRadius: baseStyles.borderRadius.md,
    overflow: 'hidden',
    position: 'relative'
  } as CSSProperties,
  progressBar: (percentage: string) =>
    ({
      position: 'absolute',
      width: percentage,
      height: '100%',
      backgroundColor: colors.success,
      borderRadius: '4px 0 0 4px'
    }) as CSSProperties,
  progressText: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#212529',
    fontWeight: 'bold',
    fontSize: baseStyles.fontSize.sm
  } as CSSProperties,
  profilerData: {
    backgroundColor: colors.lightGray,
    padding: baseStyles.padding.xxl,
    borderRadius: baseStyles.borderRadius.md
  } as CSSProperties,
  profilerPre: {
    margin: 0,
    fontSize: baseStyles.fontSize.sm
  } as CSSProperties
};

// SQL view styles
export const sqlViewStyles = {
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  } as CSSProperties,
  tableHeader: {
    backgroundColor: colors.lightGray,
    textAlign: 'left'
  } as CSSProperties,
  tableHeaderCell: {
    padding: baseStyles.padding.lg,
    borderBottom: `1px solid ${colors.border}`
  } as CSSProperties,
  tableRow: (isEven: boolean) =>
    ({
      backgroundColor: isEven ? colors.lightGray : colors.white
    }) as CSSProperties,
  tableCell: {
    padding: baseStyles.padding.lg,
    verticalAlign: 'top',
    borderBottom: `1px solid ${colors.border}`
  } as CSSProperties,
  queryContainer: (isDuplicate: boolean, isLongDuration: boolean) =>
    ({
      color: isDuplicate ? '#ff6b6b' : 'inherit',
      fontWeight: isLongDuration ? 'bold' : 'normal'
    }) as CSSProperties,
  queryPre: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    margin: '0',
    fontSize: baseStyles.fontSize.sm,
    padding: '0'
  } as CSSProperties,
  stackTrace: {
    fontSize: baseStyles.fontSize.sm,
    color: colors.darkGray,
    whiteSpace: 'pre-wrap'
  } as CSSProperties
};
